import React, {
  KeyboardEvent,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import {
  compassLightGreen,
  darkGreen,
  greyText,
  lightGreyOutline,
  lightGreyText,
} from "../../styles";
import { Avatar } from "./Avatar";
import { EASE, popIn, reducedMotion } from "./motion";

export enum SignalIconSize {
  TAB = "xsmall",
  PANEL = "default",
}

export const SIGNAL_ICON_PX: Record<SignalIconSize, number> = {
  [SignalIconSize.TAB]: 18,
  [SignalIconSize.PANEL]: 32,
};

export interface SignalTabItem {
  key: string;
  label: string;
  kind: string;
  renderIcon: (size: SignalIconSize) => JSX.Element;
  detail: JSX.Element;
}

const Strip = styled.div`
  position: relative;
  display: flex;
  gap: 20px;
  margin-top: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  box-shadow: inset 0 -1px 0 ${lightGreyOutline};

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Underline = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  height: 1px;
  border-radius: 1px;
  background: ${compassLightGreen};
  transition:
    transform 0.32s ${EASE},
    width 0.32s ${EASE};
  pointer-events: none;
  ${reducedMotion}
`;

const Tab = styled.button<{ $active: boolean; $index: number }>`
  all: unset;
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 7px;
  height: 40px;
  box-sizing: border-box;
  font-family: "Apercu";
  font-size: 12.5px;
  white-space: nowrap;
  cursor: pointer;
  color: ${({ $active }): string => ($active ? darkGreen : greyText)};
  transition: color 0.18s ease;
  animation: ${popIn} 0.3s ${EASE} both;
  animation-delay: ${({ $index }): number => 60 + $index * 45}ms;
  ${reducedMotion}

  .ant-avatar {
    transition:
      opacity 0.18s ease,
      filter 0.18s ease;
    opacity: ${({ $active }): number => ($active ? 1 : 0.75)};
    filter: ${({ $active }): string => ($active ? "none" : "saturate(0.5)")};
  }

  &:hover {
    color: ${darkGreen};
  }
  &:hover .ant-avatar {
    opacity: 1;
    filter: none;
  }
  &:focus-visible {
    outline: 2px solid ${compassLightGreen};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const Stack = styled.div`
  display: grid;
`;

const Panel = styled.div<{ $active: boolean }>`
  grid-area: 1 / 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 16px;
  opacity: ${({ $active }): number => ($active ? 1 : 0)};
  transform: translateY(${({ $active }): number => ($active ? 0 : 4)}px);
  visibility: ${({ $active }): string => ($active ? "visible" : "hidden")};
  transition:
    opacity 0.22s ease,
    transform 0.28s ${EASE},
    visibility 0s linear ${({ $active }): string => ($active ? "0s" : "0.22s")};
  ${reducedMotion}
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const PanelTitle = styled.span`
  flex-shrink: 0;
  font-family: "ApercuMedium";
  font-size: 13.5px;
  color: ${darkGreen};
  white-space: nowrap;
`;

const PanelKind = styled.span`
  min-width: 0;
  flex: 1;
  font-family: "Apercu";
  font-size: 12px;
  color: ${lightGreyText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Keys = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
`;

const Kbd = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 5px;
  box-sizing: border-box;
  background: white;
  box-shadow:
    inset 0 0 0 1px ${lightGreyOutline},
    0 1px 0 ${lightGreyOutline};
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 10px;
  color: ${greyText};
`;

export const avatarIcon = (
  username: string,
  image: string,
): SignalTabItem["renderIcon"] => {
  const AvatarIcon = (size: SignalIconSize): JSX.Element => (
    <Avatar username={username} image={image} size={size} />
  );
  return AvatarIcon;
};

interface UnderlineBox {
  x: number;
  y: number;
  w: number;
}

interface SignalTabsProps {
  items: SignalTabItem[];
  ariaLabel?: string;
  initialIndex?: number;
}

export const SignalTabs = ({
  items,
  ariaLabel = "Details",
  initialIndex = 0,
}: SignalTabsProps): JSX.Element | null => {
  const baseId = useId();
  const [selected, setSelected] = useState<number>(initialIndex);
  const [underline, setUnderline] = useState<UnderlineBox | null>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);

  const activeIndex = Math.min(selected, items.length - 1);

  useLayoutEffect(() => {
    const measure = (): void => {
      const el = tabs.current[activeIndex];
      if (!el) return;
      setUnderline({
        x: el.offsetLeft,
        y: el.offsetTop + el.offsetHeight - 1,
        w: el.offsetWidth,
      });
    };
    measure();
    tabs.current[activeIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
    const strip = stripRef.current;
    if (!strip || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(strip);
    return (): void => observer.disconnect();
  }, [activeIndex]);

  if (items.length === 0) return null;

  const tabId = (key: string): string => `${baseId}-tab-${key}`;
  const panelId = (key: string): string => `${baseId}-panel-${key}`;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (step === 0) return;
    e.preventDefault();
    const next = (activeIndex + step + items.length) % items.length;
    setSelected(next);
    tabs.current[next]?.focus();
  };

  return (
    <>
      <Strip
        ref={stripRef}
        role="tablist"
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
      >
        {underline && (
          <Underline
            aria-hidden="true"
            style={{
              width: underline.w,
              transform: `translate(${underline.x}px, ${underline.y}px)`,
            }}
          />
        )}
        {items.map((item, i) => (
          <Tab
            key={item.key}
            ref={(el): void => {
              tabs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={tabId(item.key)}
            aria-selected={i === activeIndex}
            aria-controls={panelId(item.key)}
            tabIndex={i === activeIndex ? 0 : -1}
            $active={i === activeIndex}
            $index={i}
            onClick={(): void => setSelected(i)}
          >
            {item.renderIcon(SignalIconSize.TAB)}
            {item.label}
          </Tab>
        ))}
      </Strip>
      <Stack>
        {items.map((item, i) => (
          <Panel
            key={item.key}
            $active={i === activeIndex}
            id={panelId(item.key)}
            role="tabpanel"
            aria-labelledby={tabId(item.key)}
            aria-hidden={i !== activeIndex}
          >
            <PanelHead>
              {item.renderIcon(SignalIconSize.PANEL)}
              <PanelTitle>{item.label}</PanelTitle>
              <PanelKind>{item.kind}</PanelKind>
              {items.length > 1 && (
                <Keys aria-hidden="true">
                  <Kbd>←</Kbd>
                  <Kbd>→</Kbd>
                </Keys>
              )}
            </PanelHead>
            {item.detail}
          </Panel>
        ))}
      </Stack>
    </>
  );
};
